import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { DashboardSummary } from '../../types/dashboard.types';

interface DashboardHeroSectionProps {
  summary: DashboardSummary;
}

/**
 * Get color class based on compliance rate thresholds.
 * <90% = red, 90-95% = amber, >95% = green
 */
function getComplianceColor(rate: number | null): string {
  if (rate === null) return 'text-gray-500';
  if (rate < 90) return 'text-red-600';
  if (rate < 95) return 'text-amber-600';
  return 'text-green-600';
}

/**
 * Get background color based on compliance rate thresholds.
 */
function getComplianceBgColor(rate: number | null): string {
  if (rate === null) return 'bg-gray-100';
  if (rate < 90) return 'bg-red-50';
  if (rate < 95) return 'bg-amber-50';
  return 'bg-green-50';
}

/**
 * Get icon background color based on violation count.
 */
function getViolationBgColor(count: number): string {
  if (count === 0) return 'bg-green-500';
  if (count < 5) return 'bg-amber-500';
  return 'bg-red-500';
}

/**
 * Get usage bar color based on percentage.
 * >80% = amber, >100% = red
 */
function getUsageColor(percentage: number): string {
  if (percentage > 100) return 'bg-red-500';
  if (percentage > 80) return 'bg-amber-500';
  return 'bg-indigo-500';
}

/**
 * Hero section displaying key compliance metrics.
 * Shows compliance rate, violations count, and usage.
 */
const DashboardHeroSection: React.FC<DashboardHeroSectionProps> = ({ summary }) => {
  const { t } = useTranslation();

  const complianceRate = summary.compliance_pass_rate;
  const changePercent = summary.compliance_change_percent;
  const violationsCount = summary.compliance_violations_count;
  const minutesUsed = parseFloat(summary.minutes_used);
  const minutesIncluded = parseFloat(summary.minutes_included);
  const usagePercentage = summary.usage_percentage;

  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {/* Compliance Rate Card */}
      <div className={`relative overflow-hidden rounded-lg ${getComplianceBgColor(complianceRate)} px-4 py-5 shadow sm:px-6`}>
        <dt>
          <div className="absolute rounded-md bg-indigo-500 p-3">
            <ShieldCheckIcon className="h-6 w-6 text-white" aria-hidden="true" />
          </div>
          <p className="ml-16 truncate text-sm font-medium text-gray-500">
            {t('dashboard:hero.complianceRate')}
          </p>
        </dt>
        <dd className="ml-16 flex items-baseline">
          <p className={`text-2xl font-semibold ${getComplianceColor(complianceRate)}`}>
            {complianceRate !== null ? `${complianceRate}%` : '-'}
          </p>
          {changePercent !== null && (
            <p
              className={`ml-2 flex items-baseline text-sm font-semibold ${
                changePercent >= 0 ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {changePercent >= 0 ? (
                <ArrowUpIcon className="h-4 w-4 flex-shrink-0 self-center" aria-hidden="true" />
              ) : (
                <ArrowDownIcon className="h-4 w-4 flex-shrink-0 self-center" aria-hidden="true" />
              )}
              <span className="ml-1">
                {changePercent >= 0 ? '+' : ''}{changePercent}%
              </span>
              <span className="sr-only">
                {changePercent >= 0 ? t('dashboard:hero.increased') : t('dashboard:hero.decreased')}
              </span>
            </p>
          )}
        </dd>
        <p className="ml-16 text-xs text-gray-500 mt-1">
          {t('dashboard:hero.periodDays', { days: summary.period_days })}
        </p>
      </div>

      {/* Violations Card */}
      {violationsCount > 0 ? (
        <Link
          to="/analyses?complianceFlagged=true&source=dashboard"
          className="relative overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:px-6 hover:shadow-md hover:ring-2 hover:ring-indigo-500 transition-all cursor-pointer block group"
        >
          <dt>
            <div className={`absolute rounded-md ${getViolationBgColor(violationsCount)} p-3`}>
              <ExclamationTriangleIcon className="h-6 w-6 text-white" aria-hidden="true" />
            </div>
            <p className="ml-16 truncate text-sm font-medium text-gray-500">
              {t('dashboard:hero.violations')}
            </p>
          </dt>
          <dd className="ml-16 flex items-baseline">
            <p className="text-2xl font-semibold text-red-600">
              {violationsCount}
            </p>
            <span className="ml-2 inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
              {t('dashboard:hero.needsReview')}
            </span>
          </dd>
          <div className="ml-16 flex items-center justify-between mt-1">
            <p className="text-xs text-gray-500">
              {summary.calls_completed} {t('dashboard:hero.callsAnalyzed')}
            </p>
            <span className="text-xs text-indigo-600 flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
              {t('dashboard:hero.viewCalls')}
              <ArrowRightIcon className="h-3 w-3 ml-1" />
            </span>
          </div>
        </Link>
      ) : (
        <div className="relative overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:px-6">
          <dt>
            <div className={`absolute rounded-md ${getViolationBgColor(violationsCount)} p-3`}>
              <ExclamationTriangleIcon className="h-6 w-6 text-white" aria-hidden="true" />
            </div>
            <p className="ml-16 truncate text-sm font-medium text-gray-500">
              {t('dashboard:hero.violations')}
            </p>
          </dt>
          <dd className="ml-16 flex items-baseline">
            <p className="text-2xl font-semibold text-green-600">
              {violationsCount}
            </p>
          </dd>
          <p className="ml-16 text-xs text-gray-500 mt-1">
            {summary.calls_completed} {t('dashboard:hero.callsAnalyzed')}
          </p>
        </div>
      )}

      {/* Usage Card */}
      <div className="relative overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:px-6">
        <dt>
          <div className="absolute rounded-md bg-indigo-500 p-3">
            <ClockIcon className="h-6 w-6 text-white" aria-hidden="true" />
          </div>
          <p className="ml-16 truncate text-sm font-medium text-gray-500">
            {t('dashboard:hero.usage')}
          </p>
        </dt>
        <dd className="ml-16">
          <p className="text-2xl font-semibold text-gray-900">
            {Math.round(minutesUsed)}/{Math.round(minutesIncluded)} <span className="text-sm font-normal text-gray-500">{t('dashboard:hero.minutes')}</span>
          </p>
          <div className="mt-2">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`${getUsageColor(usagePercentage)} h-2 rounded-full transition-all duration-300`}
                style={{ width: `${Math.min(usagePercentage, 100)}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {usagePercentage.toFixed(1)}% {t('dashboard:hero.used')}
            </p>
          </div>
        </dd>
      </div>
    </div>
  );
};

export default DashboardHeroSection;
